'use client';

import { useLayoutEffect, useState } from 'react';
import type { FC } from 'react';
import clsx from 'clsx';

import { sanitizeHtmlString } from './sanitize-html-string';
import classes from './safe-html-renderer.module.css';

interface SafeHtmlRendererProps {
  className?: string;
  htmlString: string;
}

/**
 * ### SafeHtmlRenderer
 *
 * @description
 * Renders untrusted HTML after DOMPurify sanitization. Remaining anchors open
 * in a new tab with `rel="noopener noreferrer nofollow"`, an accessible name
 * that includes "(opens in a new tab)", and a decorative external-link glyph.
 * This applies to every call site (Highlighter guidance, CSM email preview,
 * survey HTML preview).
 *
 * Sanitization is applied after mount. DOMPurify cannot run during Node SSR,
 * so doing it in render would either throw or hydrate mismatched HTML.
 *
 * @example
 * <SafeHtmlRenderer htmlString={study.instructionsIntro} />
 *
 * @param props - Component props
 * @returns React component
 */
export const SafeHtmlRenderer: FC<SafeHtmlRendererProps> = (props) => {
  const { htmlString } = props;
  const [sanitizedHtml, setSanitizedHtml] = useState('');

  useLayoutEffect(() => {
    setSanitizedHtml(sanitizeHtmlString(htmlString));
  }, [htmlString]);

  return (
    <div
      className={clsx(classes.root, props.className)}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};
